import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { resolveCompanyLabel, searchCompanies } from '@/features/crm/companies/select';
import { CURRENCIES } from '@/features/crm/deals/constants';
import { resolveDealLabel, searchDeals } from '@/features/crm/deals/select';
import { PROPOSAL_STATUSES } from '../constants';
import type { ProposalInput } from '../types';

const schema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(255),
  company: z.string().min(1, 'Company is required'),
  deal: z.string(),
  status: z.enum(['draft', 'sent', 'accepted', 'rejected']),
  currency: z.enum(['USD', 'EUR', 'GBP', 'AUD']),
  notes: z.string(),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = ['title', 'company', 'deal', 'status', 'currency', 'notes'] as const;

interface ProposalFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  onSubmit: (values: ProposalInput) => Promise<unknown>;
  onCancel: () => void;
}

export function ProposalForm({ defaultValues, submitLabel, onSubmit, onCancel }: ProposalFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      company: '',
      deal: '',
      status: 'draft',
      currency: 'USD',
      notes: '',
      ...defaultValues,
    },
  });

  async function handleSubmit(values: FormValues) {
    const input: ProposalInput = {
      title: values.title,
      company: Number(values.company),
      deal: values.deal ? Number(values.deal) : null,
      status: values.status,
      currency: values.currency,
      notes: values.notes,
    };
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="max-w-2xl space-y-6">
        <FormSection variant="card" title="Proposal" description="What's it for?">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Proposal — Acme website" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection variant="card" title="Status" description="Where does it stand?">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PROPOSAL_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection variant="card" title="Relations" description="Who's it for?">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="company-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchCompanies}
                      resolveLabel={resolveCompanyLabel}
                      placeholder="Select a company"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="deal"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Deal</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="deal-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchDeals}
                      resolveLabel={resolveDealLabel}
                      placeholder="No deal"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection variant="card" title="Notes" description="Anything extra to capture?">
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea rows={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
